import { useRef, useState } from "react";
import { api, type Item, type Link } from "./api.ts";
import { useFocusAfter } from "./focusAfter.ts";
import { AboutPicker, aboutWords, LinkError, LinkForm, useLinkChange } from "./LinkEdits.tsx";
import { Button, Icon } from "./souvenir.ts";

// A link someone found, shown by its preview: the cover, the title, the site and who made it, and the
// note it was saved with. Opens the original in a new tab. With `trip`, on a trip that can change, its own changes
// under it (DESIGN.md, Views, the web's own edits, and "What the web adds, it can take away"; features/links.md,
// "Remove a link on the web" and "Add, edit and remove every link on the web"), for anyone in the trip: Edit (its
// address and note), About (the places, goals or options it's about, picked from the trip's `items`: once it's about
// something it leaves the inbox for their cards) and Remove.
export function LinkCard({
  link,
  trip,
  items = [],
  onChanged,
}: {
  link: Link;
  trip?: string;
  items?: Item[];
  onChanged?: () => void;
}) {
  const preview = link.preview;
  const site = preview?.site ?? new URL(link.url).hostname.replace(/^www\./, "");
  // Video covers expire on some sites; fall back to the plain tile when one does.
  const [broken, setBroken] = useState(false);
  const thumbnail = preview?.thumbnail && !broken ? preview.thumbnail : null;
  const change = useLinkChange(trip, onChanged);
  const [open, setOpen] = useState<"edit" | "about" | null>(null);
  const [chosen, setChosen] = useState<string[]>(link.items);
  const actions = useRef<HTMLSpanElement>(null);
  const title = preview?.title ?? link.url;
  // Put away: the focus back on what opened it.
  const focus = useFocusAfter(actions);
  const close = (back: "edit" | "about" | null) => {
    setOpen(null);
    change.setError(null);
    if (back) focus(`[data-link-action="${back}"]`);
  };
  return (
    <div className="tm-link-tile">
      <div className={`tm-link-card${change.can ? " has-actions" : ""}`}>
        <a className="tm-link-open" href={link.url} target="_blank" rel="noreferrer noopener">
          {thumbnail ? (
            <img
              className="tm-link-thumb"
              src={thumbnail}
              alt=""
              loading="lazy"
              referrerPolicy="no-referrer"
              onError={() => setBroken(true)}
            />
          ) : (
            <span className="tm-link-thumb is-empty" aria-hidden="true">
              <Icon name="share" size={20} />
            </span>
          )}
          <span className="tm-link-text">
            <span className="callout tm-strong tm-clamp">{title}</span>
            <span className="caption tm-muted">{[site, preview?.author].filter(Boolean).join(" · ")}</span>
            {link.note && <span className="caption">{link.note}</span>}
          </span>
        </a>
        {change.can && open === null && (
          <span className="tm-link-actions" ref={actions}>
            <Button
              variant="quiet"
              size="sm"
              type="button"
              data-link-action="edit"
              aria-label={`Edit the link ${title}`}
              onClick={() => setOpen("edit")}
            >
              Edit
            </Button>
            <Button
              variant="quiet"
              size="sm"
              type="button"
              data-link-action="about"
              aria-label={`Say what the link ${title} is about`}
              onClick={() => {
                setChosen(link.items);
                setOpen("about");
              }}
            >
              About
            </Button>
            <Button
              variant="quiet"
              size="sm"
              type="button"
              disabled={change.busy || change.offline}
              aria-label={`Remove the link ${title}`}
              onClick={() => change.run(() => api.removeLink(trip as string, link.id), "Removed the link")}
            >
              {change.busy ? "Removing…" : "Remove"}
            </Button>
          </span>
        )}
        {open === "edit" && (
          <LinkForm
            label="Address"
            ariaLabel={`Edit the link ${title}`}
            start={{ url: link.url, note: link.note, items: link.items }}
            note
            busy={change.busy}
            offline={change.offline}
            error={change.error}
            onSave={async ({ url, note }) => {
              const ok = await change.run(
                () =>
                  api.editLink(trip as string, link.id, {
                    version: link.version,
                    ...(url !== link.url ? { url } : {}),
                    ...(note !== link.note ? { note } : {}),
                  }),
                "Changed the link",
              );
              if (ok) close("edit");
            }}
            onCancel={() => close("edit")}
          />
        )}
        {open === "about" && (
          <form
            className="tm-link-form"
            aria-label={`Say what the link ${title} is about`}
            onSubmit={async (e) => {
              e.preventDefault();
              const ok = await change.run(
                () => api.editLink(trip as string, link.id, { version: link.version, items: chosen }),
                `Moved the link to ${aboutWords(chosen, items)}`,
              );
              // About something, it's on their cards, not here: nothing to put the focus back on.
              if (ok) close(null);
            }}
            onKeyDown={(e) => {
              if (e.key !== "Escape") return;
              e.preventDefault();
              e.stopPropagation();
              if (JSON.stringify(chosen) === JSON.stringify(link.items)) close("about");
            }}
          >
            <AboutPicker items={items} chosen={chosen} onChange={setChosen} />
            {change.offline && (
              <p className="caption tm-muted tm-link-form-note">No signal: changing it needs signal.</p>
            )}
            <LinkError error={change.error} />
            <span className="tm-note-actions">
              <Button
                variant="secondary"
                size="sm"
                type="submit"
                disabled={change.busy || change.offline || chosen.length === 0}
              >
                {change.busy ? "Saving…" : "Save"}
              </Button>
              <Button variant="quiet" size="sm" type="button" onClick={() => close("about")}>
                Cancel
              </Button>
            </span>
          </form>
        )}
      </div>
      {open === null && <LinkError error={change.error} />}
    </div>
  );
}
